import { toJalaali, toGregorian } from 'jalaali-js'

/**
 * Convert Gregorian Date to Jalali string
 * @param {Date|string} date - Gregorian date
 * @param {string} formatStr - Format: 'YYYY/MM/DD', 'YYYY/MM/DD HH:mm', etc.
 * @returns {string} Formatted Jalali date string
 */
export function formatJalali(date, formatStr = 'YYYY/MM/DD') {
  if (!date) return ''
  
  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return ''
  
  const { jy, jm, jd } = toJalaali(d.getFullYear(), d.getMonth() + 1, d.getDate())
  const hours = d.getHours().toString().padStart(2, '0')
  const minutes = d.getMinutes().toString().padStart(2, '0')
  
  return formatStr
    .replace('YYYY', jy)
    .replace('MM', jm.toString().padStart(2, '0'))
    .replace('DD', jd.toString().padStart(2, '0'))
    .replace('HH', hours)
    .replace('mm', minutes)
}

/**
 * Format decimal hours (e.g. 0.71, 1.5, 8) into natural Persian "X ساعت و Y دقیقه"
 */
export function formatHoursAndMinutes(decimalHours) {
  if (decimalHours === undefined || decimalHours === null || isNaN(decimalHours)) {
    return '۰ دقیقه'
  }
  const totalMinutes = Math.round(Number(decimalHours) * 60)
  if (totalMinutes <= 0) return '۰ دقیقه'
  
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60

  if (h > 0 && m > 0) {
    return `${h} ساعت و ${m} دقیقه`
  } else if (h > 0) {
    return `${h} ساعت`
  } else {
    return `${m} دقیقه`
  }
}

/**
 * Get current Jalali date as object
 * @returns {Object} { year, month, day }
 */
export function getCurrentJalali() {
  const now = new Date()
  const { jy, jm, jd } = toJalaali(now.getFullYear(), now.getMonth() + 1, now.getDate())
  return { jy, jm, jd }
}

/**
 * Get Jalali month name in Persian
 * @param {number} month - Jalali month (1-12)
 * @returns {string} Persian month name
 */
export function getJalaliMonthName(month) {
  const months = [
    'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
    'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'
  ]
  return months[month - 1] || ''
}

/**
 * Get days in Jalali month
 * @param {number} year - Jalali year
 * @param {number} month - Jalali month (1-12)
 * @returns {number} Days in month
 */
export function getJalaliDaysInMonth(year, month) {
  if (month <= 6) return 31
  if (month <= 11) return 30
  // Esfand - check leap year
  const isLeap = ((year % 33) % 4) === 0
  return isLeap ? 30 : 29
}

/**
 * Get first day of Jalali month (0=Sunday, 6=Saturday)
 * @param {number} year - Jalali year
 * @param {number} month - Jalali month (1-12)
 * @returns {number} Day of week (0-6 in JS Date)
 */
export function getJalaliFirstDayOfWeek(year, month) {
  const { gy, gm, gd } = toGregorian(year, month, 1)
  const date = new Date(gy, gm - 1, gd)
  return date.getDay() // 0=Sunday, 6=Saturday
}

/**
 * Convert Jalali date to Gregorian Date object
 * @param {number} jy - Jalali year
 * @param {number} jm - Jalali month (1-12)
 * @param {number} jd - Jalali day (1-31)
 * @returns {Date} Gregorian Date
 */
export function jalaliToGregorian(jy, jm, jd) {
  const { gy, gm, gd } = toGregorian(jy, jm, jd)
  return new Date(gy, gm - 1, gd)
}

/**
 * Convert Jalali date to exact YYYY-MM-DD string without timezone skew
 */
export function jalaliToGregorianStr(jy, jm, jd) {
  const { gy, gm, gd } = toGregorian(jy, jm, jd)
  const m = String(gm).padStart(2, '0')
  const d = String(gd).padStart(2, '0')
  return `${gy}-${m}-${d}`
}

/**
 * Format Date or string to exact local YYYY-MM-DD string without timezone skew
 */
export function formatLocalDate(date) {
  if (!date) return ''
  if (typeof date === 'string') {
    if (date.length >= 10 && date.includes('-')) {
      return date.substring(0, 10)
    }
    date = new Date(date)
  }
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Get today in YYYY-MM-DD local string
 */
export function getTodayLocalDateStr() {
  return formatLocalDate(new Date())
}

/**
 * Parse Jalali date string to Gregorian Date
 * @param {string} str - Jalali date string (YYYY/MM/DD)
 * @returns {Date|null} Gregorian Date or null if invalid
 */
export function parseJalali(str) {
  if (!str) return null
  const parts = str.split('/')
  if (parts.length < 3) return null
  
  const jy = parseInt(parts[0], 10)
  const jm = parseInt(parts[1], 10)
  const jd = parseInt(parts[2], 10)
  
  if (isNaN(jy) || isNaN(jm) || isNaN(jd)) return null
  
  try {
    return jalaliToGregorian(jy, jm, jd)
  } catch {
    return null
  }
}

/**
 * Get weekday name in Persian
 * @param {number} day - Day of week (0=Sunday, 6=Saturday)
 * @returns {string} Persian weekday name
 */
export function getPersianWeekday(day) {
  const days = ['یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه', 'جمعه', 'شنبه']
  return days[day] || ''
}

/**
 * Format time from Date
 * @param {Date|string} date
 * @returns {string} HH:mm
 */
export function formatTime(date) {
  if (!date) return ''
  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return ''
  return d.getHours().toString().padStart(2, '0') + ':' + d.getMinutes().toString().padStart(2, '0')
}

/**
 * Format date for API (ISO string)
 * @param {Date} date
 * @returns {string} ISO date string
 */
export function formatForAPI(date) {
  return formatLocalDate(date)
}
export { toJalaali, toGregorian }
